import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { getFirestore, enableIndexedDbPersistence } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getAnalytics, isSupported as analyticsIsSupported, logEvent } from 'firebase/analytics';
import { isClientSignPage } from './sign/clientSignPath';

// These are Firebase client SDK config values — intentionally public per
// Firebase documentation (https://firebase.google.com/docs/projects/api-keys).
// They are required in the frontend bundle and cannot be kept secret.
// Access control is enforced by Firestore/Storage rules + authenticated custom
// claims, not by hiding these values. Env vars are required (no silent
// fallback) so a missing-env build fails loud at module-init rather than
// shipping a prod bundle from a staging deploy. See RW-550.
function requireEnv(key) {
  const value = process.env[key];
  if (!value || value.trim() === '') {
    throw new Error(
      `Missing required env var: ${key}\n` +
      `Did you mean to run 'npm run build:staging' or 'npm run build:prod'?\n` +
      `Plain 'npm run build' does NOT load environment-specific config.`
    );
  }
  return value;
}

const firebaseConfig = {
  apiKey: requireEnv('REACT_APP_FIREBASE_API_KEY'),
  authDomain: requireEnv('REACT_APP_FIREBASE_AUTH_DOMAIN'),
  projectId: requireEnv('REACT_APP_FIREBASE_PROJECT_ID'),
  storageBucket: requireEnv('REACT_APP_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: requireEnv('REACT_APP_FIREBASE_MESSAGING_SENDER_ID'),
  appId: requireEnv('REACT_APP_FIREBASE_APP_ID'),
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || '',
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const provider = new GoogleAuthProvider();
export const db = getFirestore(app);

// Enable Firestore offline persistence (best effort).
if (typeof window !== 'undefined') {
  enableIndexedDbPersistence(db).catch((err) => {
    if (err && err.code === 'failed-precondition') {
      console.warn('Firestore persistence failed: multiple tabs open');
    } else if (err && err.code === 'unimplemented') {
      console.warn('Firestore persistence not supported in this browser');
    }
  });
}
export const storage = getStorage(app);
export const functions = getFunctions(app);

// RW-953: build a Cloud Functions HTTPS endpoint URL for the CURRENT project,
// for the few places that fetch() a function directly instead of using the
// callable SDK (e.g. webhook display, token-gated public pages). Derived from
// the active projectId so it auto-targets staging vs prod instead of hardcoding
// `us-central1-reve-intranet.cloudfunctions.net`. Region matches the
// getFunctions(app) default (us-central1); override the whole base with
// REACT_APP_FUNCTIONS_BASE_URL if a function is ever moved/rewritten.
const FUNCTIONS_REGION = 'us-central1';
export const functionsBaseUrl = (name = '') => {
  const base =
    process.env.REACT_APP_FUNCTIONS_BASE_URL ||
    `https://${FUNCTIONS_REGION}-${firebaseConfig.projectId}.cloudfunctions.net`;
  return name ? `${base}/${name}` : base;
};

// Messaging is only supported in browsers that expose the Notification API.
// Wrap in try/catch since getMessaging() throws in unsupported environments.
let messaging = null;
try {
  if (typeof window !== 'undefined' && 'Notification' in window) {
    messaging = getMessaging(app);
  }
} catch {
  // Firebase Messaging is not supported in this environment.
}

export { messaging, getToken, onMessage };

// ── Firebase Analytics (GA4) ────────────────────────────────────────────
// Analytics requires the `measurementId` (set in firebaseConfig above) and
// a browser environment. `isSupported()` returns false in environments
// without the required APIs (SSR, very old browsers, some embedded webviews).
// We initialize once and expose `logAnalyticsEvent()` as a safe wrapper that
// no-ops if analytics never came up — analytics must NEVER throw or block
// page rendering.
let _analytics = null;
// RW-3278: not on the client signing page — GA4's automatic page_view would
// send its URL, and the slug in that URL is the signer's signing link.
if (typeof window !== 'undefined' && !isClientSignPage()) {
  analyticsIsSupported()
    .then((supported) => {
      if (supported) {
        try { _analytics = getAnalytics(app); } catch { /* swallow */ }
      }
    })
    .catch(() => { /* swallow */ });
}

export function logAnalyticsEvent(eventName, params = {}) {
  if (!_analytics) return;
  try {
    logEvent(_analytics, eventName, params);
  } catch {
    // Never let analytics crash the app.
  }
}

provider.setCustomParameters({
  hd: 'reverealtors.com',
  prompt: 'select_account',
});

// Firebase Storage security rules must be configured in the Firebase Console.
// If you see "unauthorized" errors when uploading or loading files, update your
// Storage rules at: https://console.firebase.google.com/project/reve-intranet/storage/rules
//
// Recommended rules for authenticated access:
//   rules_version = '2';
//   service firebase.storage {
//     match /b/{bucket}/o {
//       match /{allPaths=**} {
//         allow read, write: if request.auth != null;
//       }
//     }
//   }
// NOTE: If Storage returns "unauthorized", update rules in Firebase Console → Storage → Rules
// to allow authenticated access. See recommended rules above.

export { signInWithPopup, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail };
export default app;
